// PasswordField.jsx - Password input with visibility toggle
import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

const PasswordField = ({
  label,
  value,
  onChange,
  error,
  name,
  id,
  required = false,
  disabled = false,
  autoComplete = 'current-password',
  showToggle = true,
  placeholder,
  className = '',
  variant = 'dark',
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputId = id || name || label?.toLowerCase().replace(/\s+/g, '-');

  // The login screen renders on the white gradient (AuthLayout variant="light"),
  // where the dark fill and its near-white text would be unreadable.
  const isLight = variant === 'light';
  const surface = isLight
    ? 'bg-white text-[#282A29] placeholder-[#828382]'
    : 'bg-[#282a29] text-[#f0f5f2] placeholder-[#5d625f]';
  const idleBorder = isLight
    ? 'border-[#E8EBE9] focus:border-bloom-green focus:ring-bloom-green/40'
    : 'border-[#5d625f] focus:border-bloom-green focus:ring-bloom-green/40';
  const toggleColor = isLight ? 'text-[#828382]' : 'text-[#5d625f]';

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className={className}>
      {/* The toggle centres on THIS box, not the outer one: the outer div also
          holds the error text, so centring there dropped the icon below the
          dots as soon as a field had an error. */}
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          id={inputId}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder || label}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          aria-label={label}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : undefined}
          className={`w-full px-4 py-3 ${
            showToggle ? 'pr-12' : 'pr-4'
          } ${surface} border ${
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/40'
              : idleBorder
          } rounded-[4px] text-[16px] font-['Inter'] focus:outline-none focus:ring-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          {...props}
        />
        {showToggle && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            disabled={disabled}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            className={`absolute right-3 top-1/2 flex -translate-y-1/2 items-center justify-center ${toggleColor} hover:text-bloom-green focus:outline-none focus:text-bloom-green transition-colors disabled:opacity-50`}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
      {error && (
        <p
          id={`${inputId}-error`}
          className="mt-1 text-sm text-red-500 font-['Inter']"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default PasswordField;
